import React from 'react';
import { followUser, unfollowUser, FollowState } from '../../../services/social/api';
import './FollowButton.css';

interface FollowButtonProps {
  userId: string;
  initialFollowing: boolean;
  /** D-35: full relationship state; wins over initialFollowing when provided. */
  initialState?: FollowState;
  /** Notifies the parent so it can adjust the follower count optimistically. */
  onChange?: (following: boolean) => void;
  className?: string;
}

/**
 * Follow / Requested / Following toggle (T2.2 + D-35 follow requests).
 * Public target: Follow ⇄ Following (optimistic). Closed target: Follow →
 * "Requested" (the server decides which), and clicking Requested cancels the
 * pending request (unfollow clears both edge kinds).
 */
const FollowButton: React.FC<FollowButtonProps> = ({
  userId,
  initialFollowing,
  initialState,
  onChange,
  className,
}) => {
  const [state, setState] = React.useState<FollowState>(
    initialState ?? (initialFollowing ? 'following' : 'none'),
  );
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setState(initialState ?? (initialFollowing ? 'following' : 'none'));
  }, [initialFollowing, initialState]);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (state === 'none') {
        // The server decides: instant follow (public) or pending request (closed).
        const result = await followUser(userId);
        setState(result);
        if (result === 'following') onChange?.(true);
      } else {
        // Following → unfollow; Requested → cancel the request. Same endpoint.
        const wasFollowing = state === 'following';
        setState('none'); // optimistic
        if (wasFollowing) onChange?.(false);
        try {
          await unfollowUser(userId);
        } catch (error) {
          setState(wasFollowing ? 'following' : 'requested'); // revert
          if (wasFollowing) onChange?.(true);
          throw error;
        }
      }
    } catch (error) {
      console.error('Follow toggle failed:', error);
    } finally {
      setBusy(false);
    }
  };

  const label = state === 'following' ? 'Following' : state === 'requested' ? 'Requested' : 'Follow';

  return (
    <button
      type="button"
      className={`v2-follow-button ${state !== 'none' ? 'v2-follow-button--following' : ''} ${className || ''}`}
      onClick={handleClick}
      disabled={busy}
      aria-pressed={state !== 'none'}
      title={state === 'requested' ? 'Request pending — click to cancel' : undefined}
    >
      {label}
    </button>
  );
};

export default FollowButton;
